import React, { useEffect, useMemo, useState } from "react";
import { api, inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Package, Warning, MagnifyingGlass, PencilSimple,
  CheckCircle
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PageExportBar from "@/components/PageExportBar";
import { openPrintStatement } from "@/lib/printStatement";
import { Printer } from "@phosphor-icons/react";

const Kpi = ({ label, value, color, foot }) => (
  <div className="kpi" data-testid={`stock-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="eyebrow">{label}</div>
    <div className="val" style={color ? { color } : undefined}>{value}</div>
    {foot ? <div className="text-[11px] font-mono mt-1 text-muted-foreground">{foot}</div> : null}
  </div>
);

export default function Stock() {
  const { activeCompanyId, activeCompany, activeStoreId } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [availableOnly, setAvailableOnly] = useState(false);

  // Reorder-level editor dialog state
  const [openReorder, setOpenReorder] = useState(false);
  const [reorderProd, setReorderProd] = useState(null);
  const [reorderValue, setReorderValue] = useState(0);

  // Stock-adjust dialog state
  const [openAdjust, setOpenAdjust] = useState(false);
  const [adjustProd, setAdjustProd] = useState(null);
  const [adjustDelta, setAdjustDelta] = useState(0);
  const [adjustNote, setAdjustNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/products${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setProducts(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId, q]);

  const enriched = useMemo(() => products.map((p) => {
    const stock = Number(p.current_stock || 0);
    const reorder = Number(p.reorder_level || 0);
    const low = reorder > 0 && stock <= reorder;
    const zero = stock <= 0;
    return { ...p, _stock: stock, _reorder: reorder, _low: low, _zero: zero };
  }), [products]);

  const filtered = useMemo(
    () => {
      let out = enriched;
      if (lowOnly) out = out.filter((p) => p._low);
      if (availableOnly) out = out.filter((p) => p._stock > 0);
      return out;
    },
    [enriched, lowOnly, availableOnly]
  );

  const totals = useMemo(() => {
    const t = { total: enriched.length, low: 0, zero: 0, available: 0, available_units: 0, value: 0 };
    enriched.forEach((p) => {
      if (p._low) t.low += 1;
      if (p._zero) t.zero += 1;
      if (p._stock > 0) { t.available += 1; t.available_units += p._stock; }
      t.value += p._stock * Number(p.purchase_price || 0);
    });
    return t;
  }, [enriched]);

  const startReorder = (p) => {
    setReorderProd(p);
    setReorderValue(p._reorder);
    setOpenReorder(true);
  };

  const saveReorder = async (e) => {
    e.preventDefault();
    if (!reorderProd) return;
    try {
      await api.patch(`/products/${reorderProd.id}`, {
        reorder_level: Number(reorderValue) || 0,
      });
      toast.success("Reorder level updated");
      setOpenReorder(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startAdjust = null;
  const saveAdjust = null;

  const exportColumns = [
    { key: "name", label: "Product" },
    { key: "sku", label: "Stock Code" },
    { key: "hsn", label: "HSN" },
    { key: "unit", label: "Unit" },
    { key: "current_stock", label: "Current Stock" },
    { key: "reorder_level", label: "Reorder Level" },
    { key: "purchase_price", label: "Purchase Price" },
    { key: "sale_price", label: "Sale Price" },
    { key: "status", label: "Status", get: (r) => r._zero ? "OUT" : r._low ? "LOW" : "OK" },
  ];

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Inventory</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Stock</h1>
          <p className="text-sm text-muted-foreground mt-1">Set reorder levels · Track low & out-of-stock items · Adjust on-hand quantities.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-9" onClick={() => {
            if (!filtered.length) { toast.error("Nothing to print"); return; }
            const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const groups = { available: [], low: [], zero: [] };
            for (const p of filtered) {
              const stock = Number(p.current_stock || 0);
              const reorder = Number(p.reorder_level || 0);
              const bucket = stock <= 0 ? "zero" : stock <= reorder ? "low" : "available";
              groups[bucket].push(p);
            }
            const buildSection = (title, rows, tone) => rows.length ? {
              heading: title,
              subheading: `${rows.length} item(s) · Inventory value <b>${fmtInr(rows.reduce((s, p) => s + Number(p.current_stock || 0) * Number(p.purchase_price || 0), 0))}</b>`,
              columns: [
                { key: "sku", label: "SKU", mono: true },
                { key: "name", label: "Product" },
                { key: "hsn", label: "HSN", mono: true },
                { key: "unit", label: "Unit" },
                { key: "on_hand", label: "On-Hand", align: "right", mono: true, tone },
                { key: "reorder", label: "Reorder At", align: "right", mono: true },
                { key: "value", label: "Value", align: "right", mono: true },
              ],
              rows: rows.map((p) => ({
                sku: p.sku || "—", name: p.name, hsn: p.hsn || "—",
                unit: p.unit || "PCS",
                on_hand: `${Number(p.current_stock || 0).toFixed(2)}`,
                reorder: `${Number(p.reorder_level || 0).toFixed(2)}`,
                value: fmtInr(Number(p.current_stock || 0) * Number(p.purchase_price || 0)),
              })),
            } : null;
            const sections = [
              buildSection("Available Stock", groups.available, ""),
              buildSection("Low Stock (At / Below Reorder)", groups.low, "neg"),
              buildSection("Out of Stock", groups.zero, "neg"),
            ].filter(Boolean);
            const totalValue = filtered.reduce((s, p) => s + Number(p.current_stock || 0) * Number(p.purchase_price || 0), 0);
            openPrintStatement({
              title: "Stock Statement",
              company: activeCompany || {},
              sections,
              totals: [
                { label: "Total Products", value: String(filtered.length) },
                { label: "Available", value: String(groups.available.length), tone: "pos" },
                { label: "Low Stock", value: String(groups.low.length), tone: "neg" },
                { label: "Out of Stock", value: String(groups.zero.length), tone: "neg" },
                { label: "Inventory Value", value: fmtInr(totalValue) },
              ],
              note: "Stock snapshot as of print date. Reorder items in the 'Low Stock' section to avoid disruptions.",
            });
          }} data-testid="stock-print-statement">
            <Printer size={16} className="mr-1.5" /> Print Statement
          </Button>
          <PageExportBar
            title="Stock"
            filename={`stock-${new Date().toISOString().slice(0,10)}`}
            columns={exportColumns}
            rows={filtered}
            testidPrefix="stock"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Kpi label="Total Products" value={totals.total} />
        <Kpi label="Available Stock" value={`${totals.available_units.toFixed(2)} units available`} color="hsl(var(--success))" foot={`${totals.available} product(s) · avl in stock`} />
        <Kpi label="Low Stock" value={totals.low} color="hsl(var(--warning))" foot="At or below reorder level" />
        <Kpi label="Out of Stock" value={totals.zero} color="hsl(var(--destructive))" foot="Zero on-hand" />
        <Kpi label="Inventory Value" value={inr(totals.value)} foot="At purchase price" />
      </div>

      <div className="card-flat p-4 flex flex-wrap items-center gap-3 no-print">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…"
            className="pl-9 h-9 w-64" data-testid="stock-search" />
        </div>
        <Button
          variant={availableOnly ? "default" : "outline"}
          size="sm"
          onClick={() => { setAvailableOnly((v) => !v); if (!availableOnly) setLowOnly(false); }}
          className="h-9"
          data-testid="stock-availableonly-toggle"
        >
          <CheckCircle size={14} className="mr-1.5" /> {availableOnly ? "Showing Available Only" : "Show Available Only"}
        </Button>
        <Button
          variant={lowOnly ? "default" : "outline"}
          size="sm"
          onClick={() => { setLowOnly((v) => !v); if (!lowOnly) setAvailableOnly(false); }}
          className="h-9"
          data-testid="stock-lowonly-toggle"
        >
          <Warning size={14} className="mr-1.5" /> {lowOnly ? "Showing Low Stock" : "Show Low Stock Only"}
        </Button>
        <div className="text-sm text-muted-foreground ml-auto">
          {filtered.length} of {enriched.length} products
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table" data-testid="stock-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>HSN</th>
              <th>Unit</th>
              <th className="num">On-hand</th>
              <th className="num">Reorder Level</th>
              <th>Status</th>
              <th className="num">Value</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center text-muted-foreground py-6">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-muted-foreground py-10">
                {lowOnly ? "No low-stock items — you're in the clear." : "No products yet."}
              </td></tr>
            ) : filtered.map((r) => {
              const status = r._zero ? "OUT" : r._low ? "LOW" : "OK";
              const statusColor = r._zero ? "hsl(var(--destructive))" : r._low ? "hsl(var(--warning))" : "hsl(var(--success))";
              return (
                <tr key={r.id} data-testid={`stock-row-${r.id}`} style={r._zero || r._low ? { background: r._zero ? "hsla(0,74%,60%,0.06)" : "hsla(30,92%,55%,0.05)" } : {}}>
                  <td>
                    <div className="font-medium flex items-center gap-1.5">
                      {(r._low || r._zero) && <Warning size={13} weight="fill" style={{ color: statusColor }} />}
                      {r.name}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono">{r.sku || "—"}</div>
                  </td>
                  <td className="font-mono text-[12.5px]">{r.hsn || "—"}</td>
                  <td>{r.unit || "PCS"}</td>
                  <td className="num font-mono" style={r._low || r._zero ? { color: statusColor, fontWeight: 600 } : {}}>
                    {r._stock.toFixed(2)}
                  </td>
                  <td className="num font-mono">{r._reorder > 0 ? r._reorder.toFixed(2) : <span className="text-muted-foreground">—</span>}</td>
                  <td>
                    <span
                      className="text-[10.5px] uppercase tracking-wider font-semibold"
                      style={{ color: statusColor }}
                      data-testid={`stock-status-${r.id}`}
                    >
                      {status === "OK" && <CheckCircle size={11} weight="fill" className="inline mr-1" style={{ color: statusColor }} />}
                      {status}
                    </span>
                  </td>
                  <td className="num font-mono">{inr(r._stock * Number(r.purchase_price || 0))}</td>
                  <td className="num" style={{ textAlign: "right" }}>
                    <Button variant="ghost" size="sm" className="h-8" onClick={() => startReorder(r)} data-testid={`stock-set-reorder-${r.id}`}>
                      <PencilSimple size={13} className="mr-1" /> Set Reorder Level
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Reorder-level dialog */}
      <Dialog open={openReorder} onOpenChange={setOpenReorder}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Set Reorder Level</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveReorder} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="eyebrow">Product</Label>
              <div className="text-sm font-medium">{reorderProd?.name}</div>
              <div className="text-[11px] text-muted-foreground">Current on-hand: <span className="font-mono">{Number(reorderProd?.current_stock || 0).toFixed(2)} {reorderProd?.unit}</span></div>
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Reorder Level (alert when stock falls at or below)</Label>
              <Input type="number" step="0.01" value={reorderValue}
                onChange={(e) => setReorderValue(e.target.value)}
                className="font-mono" data-testid="stock-reorder-input" autoFocus />
              <div className="text-[11px] text-muted-foreground">Set to 0 to disable alert.</div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenReorder(false)}>Cancel</Button>
              <Button type="submit" data-testid="stock-reorder-save">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
